import React, { useState, useMemo, useEffect } from 'react';
import { useTrackFutureValue } from '../hooks/useTrackFutureValue';
import { notifyUser } from '../util/util';
import { useModalWidth } from '../hooks/useModalWidth';
import { scrollTriggerIntoView } from '../util/modalUtils';

const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const ExplanationModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: 16,
        boxSizing: 'border-box',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          padding: 24,
          width: '100%',
          maxWidth: '600px',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          boxSizing: 'border-box',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ fontSize: 'clamp(16px, 3.5vw, 22px)', fontWeight: 700, margin: 0, color: '#00704A' }}>
            Parameter Explanations
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 22, color: '#888', cursor: 'pointer', fontWeight: 700, lineHeight: 1, padding: 4 }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <h3 style={{ color: '#00704A', marginBottom: 6, fontSize: 'clamp(13px, 2.8vw, 16px)', fontWeight: 600 }}>Initial Lump Sum</h3>
          <p style={{ color: '#555', lineHeight: 1.6, fontSize: 'clamp(12px, 2.5vw, 14px)', margin: 0 }}>
            Enter the amount you're starting with in your pension account. Example: if you already have GHS 10,000 saved, enter that here.
          </p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <h3 style={{ color: '#00704A', marginBottom: 6, fontSize: 'clamp(13px, 2.8vw, 16px)', fontWeight: 600 }}>Monthly Contribution</h3>
          <p style={{ color: '#555', lineHeight: 1.6, fontSize: 'clamp(12px, 2.5vw, 14px)', margin: 0 }}>
            Enter how much you plan to contribute every month going forward. This helps us project how your pension will grow with regular savings.
          </p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <h3 style={{ color: '#00704A', marginBottom: 6, fontSize: 'clamp(13px, 2.8vw, 16px)', fontWeight: 600 }}>Annual Interest Rate</h3>
          <p style={{ color: '#555', lineHeight: 1.6, fontSize: 'clamp(12px, 2.5vw, 14px)', margin: 0 }}>
            Enter the expected yearly interest rate (as a percentage). Example: if you expect fund to grow by 10% per year, enter "10".
          </p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <h3 style={{ color: '#00704A', marginBottom: 6, fontSize: 'clamp(13px, 2.8vw, 16px)', fontWeight: 600 }}>Years Of Retirement</h3>
          <p style={{ color: '#555', lineHeight: 1.6, fontSize: 'clamp(12px, 2.5vw, 14px)', margin: 0 }}>
            Enter how many years you plan to contribute before retirement. This helps us calculate how long your money will grow.
          </p>
        </div>
      </div>
    </div>
  );
};

const FutureValueCalculatorModal = ({ isOpen, onClose }) => {
  const modalWidth = useModalWidth();
  const [initialLumpSum, setInitialLumpSum] = useState('');
  const [monthlyContribution, setMonthlyContribution] = useState('');
  const [yearsOfRetirement, setYearsOfRetirement] = useState('');
  const [annualInterestRate, setAnnualInterestRate] = useState('');
  const [breakdown, setBreakdown] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [futureValue, setFutureValue] = useState('');

  useEffect(() => { if (isOpen) scrollTriggerIntoView(); }, [isOpen]);

  const trackMutation = useTrackFutureValue();

  const omkTextFieldStyles = useMemo(() => ({
    width: '100%',
    '--omk-textfield-height': '48px',
    '--omk-textfield-border-radius': '10px',
    '--omk-textfield-padding': '0 16px',
    '--omk-textfield-background': '#fff',
    '--omk-textfield-border-color': '#ddd',
    '--omk-textfield-hover-border-color': '#0D7C4B',
  }), []);

  const calculateFutureValue = () => {
    const A = parseFloat(initialLumpSum) || 0;
    const P = parseFloat(monthlyContribution) || 0;
    const n = parseFloat(yearsOfRetirement) || 0;
    const rAnnual = parseFloat(annualInterestRate) / 100 || 0;

    if (A < 0 || P < 0 || n <= 0 || rAnnual < 0) {
      notifyUser('error', 'Please enter valid positive numbers for all fields.', 'Validation Error');
      return;
    }

    const rMonthly = rAnnual / 12;
    const months = n * 12;

    const fvLumpSum = A * Math.pow(1 + rMonthly, months);
    let fvContributions = 0;
    if (P > 0) {
      fvContributions = P * ((Math.pow(1 + rMonthly, months) - 1) / rMonthly);
    }

    const fv = fvLumpSum + fvContributions;
    const totalContributions = A + P * months;
    const interestEarned = fv - totalContributions;

    const calculatedFutureValue = fv.toFixed(2);

    setFutureValue(calculatedFutureValue);
    setBreakdown({
      totalContributions: totalContributions.toFixed(2),
      interestEarned: interestEarned.toFixed(2),
    });

    trackMutation.mutate({
      inputs: [initialLumpSum, monthlyContribution, yearsOfRetirement, annualInterestRate],
      result: calculatedFutureValue,
    });
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 16,
          boxSizing: 'border-box',
        }}
        onClick={onClose}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 16,
            width: '100%',
            maxWidth: modalWidth,
            maxHeight: '85vh',
            overflowY: 'auto',
            padding: 0,
            boxShadow: '0 16px 60px rgba(0,0,0,0.15)',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {/* Header */}
          <div style={{ padding: '24px 24px 0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ fontSize: 'clamp(16px, 3.5vw, 22px)', fontWeight: 700, margin: 0, color: '#00704A' }}>
              Future Value Calculator
            </h2>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', fontSize: 24, color: '#888', cursor: 'pointer', fontWeight: 700, lineHeight: 1, padding: 4, flexShrink: 0 }}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div style={{ padding: '20px 24px 24px 24px', flex: 1, overflowY: 'auto' }}>
            <div style={{ marginBottom: 16 }}>
              <omk-text-field
                label="Initial Lump Sum (GHS)"
                name="initialLumpSum"
                type="number"
                value={initialLumpSum}
                onInput={(e) => setInitialLumpSum(e.target.value)}
                style={omkTextFieldStyles}
                placeholder="Enter initial lump sum"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <omk-text-field
                label="Monthly Contribution (GHS)"
                name="monthlyContribution"
                type="number"
                value={monthlyContribution}
                onInput={(e) => setMonthlyContribution(e.target.value)}
                style={omkTextFieldStyles}
                placeholder="Enter monthly contribution"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <omk-text-field
                label="Years Of Retirement"
                name="yearsOfRetirement"
                type="number"
                value={yearsOfRetirement}
                onInput={(e) => setYearsOfRetirement(e.target.value)}
                style={omkTextFieldStyles}
                placeholder="Enter years of retirement"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <omk-text-field
                label="Annual Interest Rate (%)"
                name="annualInterestRate"
                type="number"
                value={annualInterestRate}
                onInput={(e) => setAnnualInterestRate(e.target.value)}
                style={omkTextFieldStyles}
                placeholder="Enter annual interest rate"
              />
            </div>

            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <span
                style={{ color: '#00704A', cursor: 'pointer', textDecoration: 'underline', fontSize: 14 }}
                onClick={() => setShowExplanation(true)}
              >
                See Explanation Of Parameters
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <button
                onClick={calculateFutureValue}
                className="btn btn-primary mobile-full-width"
                style={{ padding: '10px 16px', fontSize: 14, fontWeight: 600, maxWidth: 300 }}
              >
                Calculate Future Value
              </button>
            </div>

            {futureValue && (
              <div style={{ marginBottom: 8, wordBreak: 'break-word' }}>
                <h3 style={{ marginBottom: 8, color: '#00704A', fontSize: 'clamp(13px, 2.8vw, 16px)', fontWeight: 600 }}>Breakdown</h3>
                <div style={{ padding: 12, background: '#f9f9f9', borderRadius: 8 }}>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Future Value: GHS {formatCurrency(futureValue)}</strong>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Total Contributions: GHS {formatCurrency(breakdown.totalContributions)}</strong>
                  </div>
                  <div>
                    <strong>Interest Earned: GHS {formatCurrency(breakdown.interestEarned)}</strong>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ExplanationModal
        isOpen={showExplanation}
        onClose={() => setShowExplanation(false)}
      />
    </>
  );
};

export default FutureValueCalculatorModal;
